import * as React from "react";
import { Bell, Loader2 } from "lucide-react";
import { useMediaQuery } from "@uidotdev/usehooks";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { Skeleton } from "@/components/ui/skeleton";
import { Dater } from "@/components/Shared/Dater";
import {
  useGetUserNotificationsQuery,
  useReadUserNotificationMutation,
} from "@/api/institutional/user_api";
import { IUserNotification } from "interfaces";
import { cn } from "@/lib/utils";

const NOTIFICATION_EVENT_TITLES: { [key in string]: string } = {
  USER_STATUS_CHANGED: "Status Changed",
};

const NOTIFICATION_EVENT_MESSAGES: { [key in string]: string } = {
  USER_STATUS_CHANGED:
    "{{alias}} has changed status from {{value_from}} to {{value_to}}",
};

const NotificationList = ({
  notifications,
  isLoading,
  onRead,
  readingId,
}: {
  notifications: Array<IUserNotification>;
  isLoading: boolean;
  onRead: (notification: IUserNotification) => void;
  readingId: string | null;
}) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3 p-4">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex flex-col gap-2">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-3 w-full" />
          </div>
        ))}
      </div>
    );
  }

  if (!notifications.length) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 px-4 py-10 text-center">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
          <Bell className="h-5 w-5 text-muted-foreground" />
        </div>
        <p className="text-sm font-medium text-foreground">
          {t("You're all caught up")}
        </p>
        <p className="text-xs text-muted-foreground">
          {t("No notifications to display")}
        </p>
      </div>
    );
  }

  return (
    <ul className="max-h-[60vh] divide-y divide-border/50 overflow-y-auto md:max-h-96">
      {notifications.map((notification) => {
        const isReading = readingId === notification.id;
        return (
          <li key={notification.id}>
            <button
              type="button"
              disabled={readingId !== null}
              onClick={() => onRead(notification)}
              className={cn(
                "flex w-full flex-col gap-1 px-4 py-3 text-left transition-colors hover:bg-muted/60 disabled:opacity-60",
                isReading && "opacity-60",
              )}
            >
              <div className="flex w-full items-center justify-between gap-2">
                <span className="min-w-0 truncate text-sm font-medium text-foreground">
                  {t(NOTIFICATION_EVENT_TITLES[notification.event] ?? notification.event)}
                </span>
                {isReading ? (
                  <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" />
                ) : (
                  <span className="shrink-0 text-xs text-muted-foreground">
                    <Dater>{notification.time_created}</Dater>
                  </span>
                )}
              </div>
              <span className="text-xs text-muted-foreground break-words">
                {NOTIFICATION_EVENT_MESSAGES[notification.event]
                  ? t(NOTIFICATION_EVENT_MESSAGES[notification.event], {
                      alias: notification.alias,
                      value_from: t(notification.value_from),
                      value_to: t(notification.value_to),
                    })
                  : `${notification.value_from} → ${notification.value_to}`}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
};

const NotificationsBell = () => {
  const { t } = useTranslation();
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const [open, setOpen] = React.useState(false);

  const {
    data: notifications = [],
    isLoading,
    refetch,
  } = useGetUserNotificationsQuery(undefined);
  const [readNotification] = useReadUserNotificationMutation();
  const [readingId, setReadingId] = React.useState<string | null>(null);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) refetch();
  };

  const handleRead = async (notification: IUserNotification) => {
    setReadingId(notification.id);
    try {
      await readNotification(notification.id).unwrap();
    } catch {
      // Keep the notification visible on failure; refetch restores state.
    } finally {
      setReadingId(null);
    }
  };

  const count = notifications.length;

  const trigger = (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      aria-label={t("Notifications")}
      className="relative text-muted-foreground hover:text-foreground"
    >
      <Bell className="h-4 w-4" />
      {count > 0 && (
        <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-white">
          {count > 99 ? "99+" : count}
        </span>
      )}
    </Button>
  );

  const list = (
    <NotificationList
      notifications={notifications}
      isLoading={isLoading}
      onRead={handleRead}
      readingId={readingId}
    />
  );

  if (isDesktop) {
    return (
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        <PopoverContent align="end" className="w-80 p-0">
          <div className="border-b border-border/50 px-4 py-3">
            <p className="text-sm font-semibold text-foreground">
              {t("Notifications")}
            </p>
          </div>
          {list}
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Drawer open={open} onOpenChange={handleOpenChange}>
      <DrawerTrigger asChild>{trigger}</DrawerTrigger>
      <DrawerContent>
        <DrawerHeader className="border-b border-border/50 text-left">
          <DrawerTitle>{t("Notifications")}</DrawerTitle>
        </DrawerHeader>
        {list}
      </DrawerContent>
    </Drawer>
  );
};

export default NotificationsBell;
